import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { volassisAPI } from '../services/api';
import toast from 'react-hot-toast';

function Profile() {
  const { currentUser, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [adminOrganizations, setAdminOrganizations] = useState([]);

  useEffect(() => {
    if (currentUser) {
      loadUserProfile();
    }
  }, [currentUser]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const response = await volassisAPI.getUserData(currentUser.id);
      setUserData(response);
      setEditData({
        firstName: response.first_name || '',
        lastName: response.last_name || '',
        email: response.email || '',
        phone: response.phone || '',
        address: response.address || '',
      });

      if (response.organizations) {
        setAdminOrganizations(response.organizations);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      firstName: userData.first_name || '',
      lastName: userData.last_name || '',
      email: userData.email || '',
      phone: userData.phone || '',
      address: userData.address || '',
    });
  };

  const handleSave = async () => {
    if (!editData.firstName || !editData.lastName || !editData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        id: currentUser.id,
        firstName: editData.firstName,
        lastName: editData.lastName,
        email: editData.email,
        phone: editData.phone,
        address: editData.address,
        active: true,
      };

      const response = await volassisAPI.updateUser(updateData);
      if (response && response.output) {
        toast.success('Profile updated successfully!');
        setIsEditing(false);
        loadUserProfile(); // Refresh data
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('An error occurred while updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEditData({
      ...editData,
      [field]: value,
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>
      
      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={3}>
                <Avatar
                  sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                >
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" gutterBottom>
                    {userData?.first_name} {userData?.last_name}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body1" color="text.secondary">
                      {userData?.email}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center" mt={1}>
                    {isAdmin && (
                      <Chip
                        icon={<AdminIcon />}
                        label="Administrator"
                        color="primary"
                        size="small"
                      />
                    )}
                    <Chip
                      label="Volunteer"
                      color="secondary"
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  {!isEditing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEdit}
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Details */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={isEditing ? editData.firstName : userData?.first_name}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    disabled={!isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={isEditing ? editData.lastName : userData?.last_name}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    disabled={!isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={isEditing ? editData.email : userData?.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={!isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={isEditing ? editData.phone : userData?.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <PhoneIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={isEditing ? editData.address : userData?.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <LocationIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Stats */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Status
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Account Type
                  </Typography>
                  <Chip
                    label={isAdmin ? 'Administrator' : 'Volunteer'}
                    color={isAdmin ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>
                
                <Divider />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label="Active"
                    color="success"
                    size="small"
                  />
                </Box>
                
                <Divider />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body2">
                    {new Date().getFullYear()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Organizations */}
        {isAdmin && adminOrganizations.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Organizations
                </Typography>
                <ul>
                  {adminOrganizations.map(org => (
                    <li key={org.id}>{org.name}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Profile;
